/**
 * Role-based Configuration System
 *
 * This module provides role configurations using named constants and simple spreads.
 * Each role's config is explicitly defined for easy traceability.
 */

import { NP_ADMIN, PAYMENTS_ADMIN, PRACTICES_ADMIN, PROVIDERS_ADMIN, VIEW_ONLY } from './admin'
import {
  CORECARE_COORDINATOR_SUPERVISOR,
  DA_CORECARE_COORDINATOR,
  DA_NP_CAPACITY_PROVIDER,
  DA_NPTESTER,
  LT_CORECARE_COORDINATOR,
  LT_NP_CAPACITY_PROVIDER,
  LT_NPTESTER,
  NPTESTER_SUPERVISOR,
} from './bend'
import { DA_SUPERVISOR, DA_THERAPIST } from './da'
import { ICAS_ENGAGE_THERAPIST, ICAS_PROVIDER } from './icas'
import { LT_CFP_COACH, LT_COACH, LT_COACH_SUPERVISOR, LT_SS_COACH } from './ltCoaching'
import { LT_CLINICAL_LEAVE_EVALUATOR, LT_PRESCRIBER, LT_PRESCRIBER_SUPERVISOR, LT_SAP_EVALUATOR } from './ltPrescriber'
import { LT_DBT_TEENS_THERAPIST, LT_DBT_THERAPIST, LT_FELLOW_THERAPIST, LT_SUPERVISOR, LT_THERAPIST } from './ltTherapy'
import { INDIVIDUAL_PROVIDER, PRACTICE_PROVIDER } from './providers'
import { RoleConfig } from './types'
import { ROLES } from '../constants/appConstants'

type RoleConfigs = { [key: string]: RoleConfig }

/**
 * Role configurations mapped to ROLES constants.
 *
 * ⚠️ IMPORTANT: ORDER MATTERS! ⚠️
 * The order of roles in this object determines merge priority in authReducer.ts.
 * When a user has multiple roles, configs are merged in the order they appear here,
 * with later roles overriding properties from earlier roles.
 *
 * This order matches the original config object in appConstants.ts to preserve
 * backward compatibility with existing merge behavior.
 */
export const roleConfigs: RoleConfigs = {
  // Base Provider Roles
  [ROLES.INDIVIDUAL_PROVIDER]: INDIVIDUAL_PROVIDER,
  [ROLES.ICAS_PROVIDER]: ICAS_PROVIDER,

  // LT Therapist Roles
  [ROLES.LT_THERAPIST]: LT_THERAPIST,

  // LT Prescriber Roles
  [ROLES.LT_CLINICAL_LEAVE_EVALUATOR]: LT_CLINICAL_LEAVE_EVALUATOR,
  // SAP goes after CLE so its medicationEnabled: false wins for SAP + CLE providers
  [ROLES.LT_SAP_EVALUATOR]: LT_SAP_EVALUATOR,
  // Prescriber goes last so it re-enables medicationEnabled for SAP + prescriber providers
  [ROLES.LT_PRESCRIBER]: LT_PRESCRIBER,

  // LT Coach Roles
  [ROLES.LT_COACH]: LT_COACH,
  [ROLES.LT_CFP_COACH]: LT_CFP_COACH,
  [ROLES.LT_NSC_COACH]: LT_COACH,
  [ROLES.LT_CGT_COACH]: LT_COACH,
  [ROLES.LT_MANAGER_COACH]: LT_COACH,
  [ROLES.LT_SS_COACH]: LT_SS_COACH,

  // LT Fellow Therapist
  [ROLES.LT_FELLOW_THERAPIST]: LT_FELLOW_THERAPIST,

  // LT DBT Therapist
  [ROLES.LT_DBT_THERAPIST]: LT_DBT_THERAPIST,

  // LT DBT Teens Therapist
  [ROLES.LT_DBT_TEENS_THERAPIST]: LT_DBT_TEENS_THERAPIST,

  // LT Supervisor Roles (all share same config)
  [ROLES.LT_SUPERVISOR]: LT_SUPERVISOR,
  [ROLES.LT_PRESCRIBER_SUPERVISOR]: LT_PRESCRIBER_SUPERVISOR,
  [ROLES.LT_COACH_SUPERVISOR]: LT_COACH_SUPERVISOR,
  [ROLES.LT_SS_COACH_SUPERVISOR]: LT_COACH_SUPERVISOR,
  [ROLES.LT_NSC_COACH_SUPERVISOR]: LT_COACH_SUPERVISOR,
  [ROLES.LT_MANAGER_COACH_SUPERVISOR]: LT_COACH_SUPERVISOR,
  [ROLES.LT_AUD_THERAPIST_SUPERVISOR]: LT_SUPERVISOR,
  [ROLES.LT_FELLOW_SUPERVISOR]: LT_SUPERVISOR,
  [ROLES.LT_DBT_THERAPIST_SUPERVISOR]: LT_SUPERVISOR,
  [ROLES.LT_DBT_TEENS_THERAPIST_SUPERVISOR]: LT_SUPERVISOR,

  // Admin Roles
  [ROLES.PRACTICES_ADMIN]: PRACTICES_ADMIN,
  [ROLES.PAYMENTS_ADMIN]: PAYMENTS_ADMIN,
  [ROLES.PROVIDERS_ADMIN]: PROVIDERS_ADMIN,
  [ROLES.NP_ADMIN]: NP_ADMIN,

  // Practice Provider
  [ROLES.PRACTICE_PROVIDER]: PRACTICE_PROVIDER,

  // View Only
  [ROLES.VIEW_ONLY]: VIEW_ONLY,

  // DA Therapist Roles (all share same config)
  [ROLES.DA_COUPLES_THERAPIST]: DA_THERAPIST,
  [ROLES.DA_FAMILY_THERAPIST]: DA_THERAPIST,
  [ROLES.DA_INDIVIDUAL_THERAPIST]: DA_THERAPIST,
  [ROLES.DA_CHILD_THERAPIST]: DA_THERAPIST,
  [ROLES.DA_ADOLESCENT_THERAPIST]: DA_THERAPIST,

  // DA Supervisor Roles (all share same config)
  [ROLES.DA_INDIVIDUAL_SUPERVISOR]: DA_SUPERVISOR,
  [ROLES.DA_FAMILY_SUPERVISOR]: DA_SUPERVISOR,
  [ROLES.DA_COUPLES_SUPERVISOR]: DA_SUPERVISOR,
  [ROLES.DA_CHILD_SUPERVISOR]: DA_SUPERVISOR,
  [ROLES.DA_ADOLESCENT_SUPERVISOR]: DA_SUPERVISOR,

  // BEND Roles (NP and Core providers with simplified calendar setup)
  [ROLES.DA_CORECARE_COORDINATOR]: DA_CORECARE_COORDINATOR,
  [ROLES.LT_CORECARE_COORDINATOR]: LT_CORECARE_COORDINATOR,
  [ROLES.DA_NPTESTER]: DA_NPTESTER,
  [ROLES.LT_NPTESTER]: LT_NPTESTER,
  [ROLES.DA_NP_TESTING_PSYCHOLOGIST]: DA_NP_CAPACITY_PROVIDER,
  [ROLES.DA_NP_NEUROPSYCHOLOGIST]: DA_NP_CAPACITY_PROVIDER,
  [ROLES.DA_NP_PSYCHOMETRIST]: DA_NPTESTER,
  [ROLES.DA_NP_CARE_COORDINATOR]: DA_NPTESTER,
  [ROLES.LT_NP_TESTING_PSYCHOLOGIST]: LT_NP_CAPACITY_PROVIDER,
  [ROLES.LT_NP_NEUROPSYCHOLOGIST]: LT_NP_CAPACITY_PROVIDER,
  [ROLES.LT_NP_PSYCHOMETRIST]: LT_NPTESTER,
  [ROLES.LT_NP_CARE_COORDINATOR]: LT_NPTESTER,

  // BEND Supervisor Roles
  [ROLES.NPTESTER_SUPERVISOR]: NPTESTER_SUPERVISOR,
  [ROLES.CORECARE_COORDINATOR_SUPERVISOR]: CORECARE_COORDINATOR_SUPERVISOR,

  // ICAS Therapist Roles
  [ROLES.ICAS_INDIVIDUAL_THERAPIST]: ICAS_ENGAGE_THERAPIST,
}

// Re-export individual configs for direct access if needed
export {
  CORECARE_COORDINATOR_SUPERVISOR,
  DA_CORECARE_COORDINATOR,
  DA_NP_CAPACITY_PROVIDER,
  DA_NPTESTER,
  LT_CORECARE_COORDINATOR,
  LT_NP_CAPACITY_PROVIDER,
  LT_NPTESTER,
  NPTESTER_SUPERVISOR,
} from './bend'
export { DA_SUPERVISOR, DA_THERAPIST } from './da'
export { ICAS_PROVIDER } from './icas'
export { LT_CFP_COACH, LT_COACH, LT_COACH_SUPERVISOR, LT_SS_COACH } from './ltCoaching'
export { LT_CLINICAL_LEAVE_EVALUATOR, LT_PRESCRIBER, LT_PRESCRIBER_SUPERVISOR, LT_SAP_EVALUATOR } from './ltPrescriber'
export { LT_DBT_THERAPIST, LT_FELLOW_THERAPIST, LT_SUPERVISOR, LT_THERAPIST } from './ltTherapy'
export { INDIVIDUAL_PROVIDER, PRACTICE_PROVIDER } from './providers'
export { PAYMENTS_ADMIN, PRACTICES_ADMIN, PROVIDERS_ADMIN, VIEW_ONLY } from './admin'
export * from './calendars'
export type { RoleConfig } from './types'
